import { WARRANTY_COVER, WARRANTY_STARTS_FROM, EXPECTED_LIFE_YEARS } from '../config/warranty';
import { AWNING_FROM_PRICE } from '../config/startingPrice';
import { STOCK_COLOUR_NAME, LEAD_STOCK, LEAD_STANDARD, LEAD_LARGE, LEAD_PERGOLA, STANDARD_SIZE_MAX_M } from '../config/leadTimes';
import { TOTAL_COLOURWAYS } from './sauledaColours';

// Homepage-specific static data: FAQ accordion, per-product compare
// metadata, and the row configs for the two spec-comparison tables.
// Lifted out of HomePage.js per CQ-23 Phase A.
//
// COMPARE_DATA is keyed by product slug and consumed by both the
// awning/pergola showcase cards (tagline + bullets) and the
// spec-comparison tables (the rest of the fields).

// Canonical awning display order, by slug. Single source of truth shared
// by the homepage AwningsShowcase and the /products/awnings page so the
// two can never drift. Pinned in code (not DB sort_order) so ordering
// changes flow through the test -> prod build gate atomically and stay
// identical across the shared prod/test database. B06 leads the "most
// popular" highlight; see AwningsShowcase.
export const AWNING_ORDER = [
  // AW01 leads and carries the "Most Popular" badge.
  'aw01-full-box-awning',
  'b06-premium-open-arm-awning',
  'b05-traditional-scalloped-awning',
  'a09-long-projection-awning',
  'k3-heritage-mediterranean-awning',
];

// Slug of the awning that carries the "Most Popular" badge on the homepage
// showcase. ComparePage and AwningsPage hold their own copy of the badge in
// their per-product data - keep the three in step.
export const AWNING_POPULAR_SLUG = 'aw01-full-box-awning';

export const HOME_FAQS = [
  { question: 'Do you install?', answer: 'No. ASG is supply only across the whole range. We build to your spec and deliver. Awnings are a DIY job - about an hour with a helper, and the motor plugs into a power point so no electrician is needed - or any handyman can do it. Pergolas need your own builder; we do not attend sites or manage council permits and engineering drawings.', links: [{ to: '/for-installers', label: 'Information for your installer' }] },
  { question: 'How much does an awning or pergola cost?', answer: `Retractable awnings start from $${AWNING_FROM_PRICE} supplied, with free delivery to most of Melbourne. Pergolas are supply only and quoted to your exact dimensions and configuration. Price depends on model, size, fabric or finish, and extras such as motorisation or LED lighting. Enter your email and postcode on any product page for a written quote.` },
  { question: 'How long does it take from order to delivery?', answer: `${STOCK_COLOUR_NAME} awning fabric is held in stock and can often ship the ${LEAD_STOCK}. Everything else is made to order: ${LEAD_STANDARD} for awnings up to ${STANDARD_SIZE_MAX_M} m, ${LEAD_LARGE} above that. Aluminium pergolas are ${LEAD_PERGOLA}. Your written quote confirms the timeframe.` },
  { question: 'How do I get a quote without a site visit?', answer: 'Send photos and measurements and we advise remotely - no site visit, anywhere in Australia. Photograph the wall straight on and from both angles, plus anything in the way, and measure the available width at mounting height. We confirm the largest model that fits and send a written quote. Our measuring guide walks through it.', links: [{ to: '/measuring-guide', label: 'How to measure for your awning' }] },
  { question: 'How much wind can the awning take?', answer: 'The AW01 is wind tested by Intertek to EN 13561 Class 3, equivalent to Beaufort force 6, roughly 39 to 49 km/h. That result applies to the tested sample at 3000mm x 1530mm and does not extend to larger sizes or other models. Retract the awning above that, and in hail or snow. Damage from leaving it out is not covered.' },
  { question: `What's the warranty?`, answer: `Awnings: ${WARRANTY_COVER.awning.years} years on ${WARRANTY_COVER.awning.covers}, and built to last ${EXPECTED_LIFE_YEARS} years or more (their expected life, not a warranty term). Pergolas: ${WARRANTY_COVER.pergola.years} years on ${WARRANTY_COVER.pergola.covers}. Both run from the ${WARRANTY_STARTS_FROM}. The Dooya motor and electrical components including wind sensors and LED lighting are covered by their manufacturers, separately. Excluded: misuse, unauthorised modifications, and weather beyond design loads.` },
  { question: 'What materials do you use?', answer: `High-grade aluminium alloy with a multi-stage powder coat: UV stable, corrosion resistant, and it will not rust, warp or need repainting. Awning fabric is Spanish Sauleda acrylic in ${TOTAL_COLOURWAYS} colourways. Motors are Dooya. Frame, fabric and motor are assembled and bench-tested at our Hoppers Crossing warehouse. Installation must comply with the National Construction Code, which is your installer's responsibility.` },
  { question: 'What if it arrives damaged?', answer: 'Inspect the goods on delivery before signing, and check the packaging. Report any damage, shortage or wrong item in writing to info@asgshading.com.au within 2 business days, with photographs of the damage and the packaging. Keep the packaging until it is resolved - carriers often need it to assess a claim.', links: [{ to: '/terms', label: 'Full terms' }] },
  { question: 'Can you help me find an installer?', answer: 'We do not employ or endorse installers and we do not publish a list, but tell us your suburb and what you have ordered and we will point you in a sensible direction. Our installer page has the manual, the wind report, and everything a tradesperson needs to quote the job.', links: [{ to: '/for-installers', label: 'Information for your installer' }] },
  { question: 'Do I need a permit for a pergola in Melbourne?', answer: 'Usually yes - most Melbourne councils require a building permit for a permanent pergola. Requirements depend on your council, the size, boundary setbacks, and whether it is attached to the house. Heritage overlays, bushfire and flood zones add conditions. ASG does not manage permits. Speak to your council or a building surveyor before ordering; we supply the specifications they need.' },
  { question: 'Do you offer financing or payment plans?', answer: 'Standard orders are paid in full at checkout. Large or custom orders can be paid in full, or with a 50% deposit to confirm the order and lock in your specifications and the balance before dispatch. Your written quote sets out which applies. We do not offer financing or instalment plans.' },
];

// Homepage product-card display data, keyed by slug.
//
// `feature`  - the second label burned over the main image. The FIRST label is
//              the operation ("Motorised" / "Manual"), derived in ProductCard
//              from COMPARE_DATA's `motorised` flag - as is the third spec
//              tile. Deriving both means the overlay, the tile and the bullet
//              list cannot contradict each other, which they did on first
//              build when the operation was typed in by hand here.
// `tiles`    - the two feature tiles under the bullets. Each is {top, bottom}
//              so the small grey line sits above the bold value, as in the
//              reference.
//
// Kept next to COMPARE_DATA so a product's card copy lives in one place.
export const CARD_DISPLAY = {
  'aw01-full-box-awning': {
    // The card keeps these four shots wherever a card is drawn (see
    // utils/productImages.js). The product page has the full gallery; the
    // card is meant to match its four siblings, and its first image is the
    // plain shot of the awning rather than the gallery's lifestyle hero.
    cardImages: [
      '/images/products/awnings/awning-aw01-classical-stock-v5.webp',
      '/uploads/products/aw01-clean.webp',
      '/uploads/products/aw01-g-09-family-dining.webp',
      '/uploads/products/aw01-restaurant-people.webp',
    ],
    blurb: 'Our fully enclosed cassette model seals the fabric and folding arms inside a sealed aluminium box when retracted, with integrated LED lighting and motorised control.',
    feature: 'Full Cassette',
    tiles: [
      { top: `${TOTAL_COLOURWAYS} Colours`, bottom: '2.0 to 7.0 m', icon: 'colours' },
      { top: 'Integrated', bottom: 'LED Lighting', icon: 'led' },
    ],
  },
  'b06-premium-open-arm-awning': {
    blurb: 'Our new-generation open-arm awning links its stainless steel arms together for a more rigid frame, with waterproof LED strips built into the arms themselves.',
    feature: 'LED In Arms',
    tiles: [
      { top: `${TOTAL_COLOURWAYS} Colours`, bottom: '2.0 to 6.0 m', icon: 'colours' },
      { top: 'In-Arm', bottom: 'LED Lighting', icon: 'led' },
    ],
  },
  'a09-long-projection-awning': {
    blurb: 'A previous-generation design built for reach, with heavy-gauge 82 mm independent arms and double-plate bracket mounting for projections up to 4 metres.',
    feature: '4 m Projection',
    tiles: [
      { top: `${TOTAL_COLOURWAYS} Colours`, bottom: '2.0 to 6.0 m', icon: 'colours' },
      { top: 'Deepest', bottom: '4 m Projection', icon: 'projection' },
    ],
  },
  'b05-traditional-scalloped-awning': {
    blurb: 'A new-generation traditional awning with linked stainless steel arms and a decorative scalloped valance, in your choice of white or frosted black.',
    feature: 'Scalloped Valance',
    tiles: [
      { top: `${TOTAL_COLOURWAYS} Colours`, bottom: '2.0 to 6.0 m', icon: 'colours' },
      { top: 'Scalloped', bottom: 'Valance', icon: 'valance' },
    ],
  },
  'k3-heritage-mediterranean-awning': {
    blurb: 'A previous-generation heritage design with curved aluminium arms and a pointed-tab valance, suited to Federation, period weatherboard and Mediterranean homes.',
    feature: 'Heritage Profile',
    tiles: [
      { top: `${TOTAL_COLOURWAYS} Colours`, bottom: '2.0 to 6.0 m', icon: 'colours' },
      { top: 'Pointed', bottom: 'Valance Tabs', icon: 'valance' },
    ],
  },
};

// `linkedArms` distinguishes the B series (arms chain-linked to each other)
// from the A and K series (independent arms). AW01 counts as linked, though its
// cassette is a separate style rather than a B-series arm set. Drives the arms
// spec tile on the homepage card.
export const COMPARE_DATA = {
  'aw01-full-box-awning': {
    tagline: 'The Cassette',
    bullets: [
      'Motorised (electric)',
      'Fully enclosed cassette + integrated LED',
      'Up to 7 m × 3.5 m',
      '37 Spanish Sauleda fabric colours',
      'Free delivery to most of Melbourne',
    ],
    aesthetic: 'Modern, fully enclosed',
    valance: 'None (cassette)',
    led: 'Integrated',
    motorised: true,
    cassette: true,
    armMech: 'Stainless chain link',
    linkedArms: true,
    maxSize: '7.0 × 3.5 m',
    widthRange: '2.0 to 7.0 m',
    projRange: '1.5 to 3.5 m',
  },
  'b06-premium-open-arm-awning': {
    tagline: 'Premium Open-Arm',
    // The card image, used wherever this product appears as a card or
    // thumbnail (see utils/productImages.js). The product detail page keeps
    // its own gallery order.
    cardImage: '/uploads/gallery/image-b06-thumbnail.webp',
    bullets: [
      'Motorised (electric)',
      'LED strips integrated in the arms',
      'Stainless steel chain link joints',
      'Up to 6 m × 3 m',
      'Free delivery to most of Melbourne',
    ],
    aesthetic: 'Modern open + LED',
    valance: 'None - clean front bar',
    led: 'In arms',
    motorised: true,
    cassette: false,
    armMech: 'Stainless chain link',
    linkedArms: true,
    generation: 'new',
    maxSize: '6.0 × 3 m',
    widthRange: '2.0 to 6.0 m',
    projRange: '1.5 to 3 m',
  },
  'a09-long-projection-awning': {
    tagline: 'Deepest Projection',
    bullets: [
      'Manual operation',
      'Up to 6 m × 4 m - deepest in range',
      'Reinforced heavy-gauge 82 mm arms',
      'Double plate bracket mounting',
      'Free delivery to most of Melbourne',
    ],
    aesthetic: 'Heavy-gauge arms, traditional',
    valance: 'Straight + corner cutouts',
    led: 'Not available',
    motorised: false,
    cassette: false,
    armMech: 'Independent heavy-gauge arms',
    linkedArms: false,
    generation: 'previous',
    maxSize: '6.0 × 4 m',
    widthRange: '2.0 to 6.0 m',
    projRange: '1.5 to 4 m',
  },
  'b05-traditional-scalloped-awning': {
    tagline: 'Colour Choice',
    // The card image, used wherever this product appears as a card or
    // thumbnail (see utils/productImages.js). The product detail page keeps
    // its own gallery order.
    cardImage: '/uploads/gallery/image-b05-thumbnail-v1.webp',
    bullets: [
      'Manual operation',
      'White or frosted black finish',
      'Scalloped traditional valance',
      'Up to 6 m × 3 m',
      'Free delivery to most of Melbourne',
    ],
    aesthetic: 'Traditional + colour choice',
    valance: 'Scalloped',
    led: 'Not available',
    motorised: false,
    cassette: false,
    armMech: 'Stainless chain link',
    linkedArms: true,
    generation: 'new',
    maxSize: '6.0 × 3 m',
    widthRange: '2.0 to 6.0 m',
    projRange: '1.5 to 3 m',
  },
  'k3-heritage-mediterranean-awning': {
    tagline: 'Heritage',
    bullets: [
      'Manual operation',
      'Mediterranean pointed-tab valance',
      'Suits Federation + period homes',
      'Up to 6 m × 3 m',
      'Free delivery to most of Melbourne',
    ],
    aesthetic: 'Mediterranean / period',
    valance: 'Pointed / triangular tabs',
    led: 'Not available',
    motorised: false,
    cassette: false,
    armMech: 'Double wire link',
    linkedArms: false,
    generation: 'previous',
    maxSize: '6.0 × 3 m',
    widthRange: '2.0 to 6.0 m',
    projRange: '1.5 to 3 m',
  },
  'pergola-163': {
    tagline: 'The Compact',
    bullets: [
      'Compact louvered roof',
      'Set sizes only: 3×3, 4×3, 4×4, 3×6 m',
      '163 mm louvre blades',
    ],
    controls: 'Electric Louvers',
    setupTime: '3 to 6 hrs',
    motorised: true,
    led: 'LED Strips',
    loadCapacity: 'Standard',
    bladeWidth: '163 mm',
    maxSize: '4 m × 4 m',
    drainage: 'Hidden',
    postSize: '120×120 mm',
    custom: '3×3 · 3×4 · 3×6 · 4×4',
    retractable: false,
  },
  'pergola-175': {
    tagline: 'The Standard',
    bullets: [
      'Best-balance louvered pergola',
      'Customisable sizes',
      '175 mm blades, dual hidden drainage',
    ],
    controls: 'Electric Louvers',
    setupTime: '3 to 7 hrs',
    motorised: true,
    led: 'LED Strips',
    loadCapacity: '100 kg/m²',
    bladeWidth: '175 mm',
    maxSize: '5 m × 4 m',
    drainage: 'Dual hidden',
    postSize: '150×150 / 180×180 mm',
    custom: 'Fully custom',
    retractable: false,
  },
  'pergola-180-retractable': {
    tagline: 'The Retractable',
    bullets: [
      'Roof folds fully open to sky',
      'Customisable sizes',
      '181 mm blades + LED',
    ],
    controls: 'Smart Controls',
    setupTime: '4 to 8 hrs',
    motorised: true,
    led: 'LED Strips',
    loadCapacity: '100 kg/m²',
    bladeWidth: '181 mm',
    maxSize: '8 m × 4 m',
    drainage: 'Hidden',
    postSize: 'Aluminium alloy',
    custom: 'Fully custom',
    retractable: true,
  },
};

// NOTE: COMPARE_ROWS / AWNING_COMPARE_ROWS were removed with the homepage
// "Compare the range, side-by-side" spec tables (SpecComparison). The
// dedicated /products/compare page has its own row definitions and is
// unaffected. COMPARE_DATA below is still used by the product cards.
